import useForm from "antd/es/form/hooks/useForm";
import { useCallback, useState } from "react";
import request from "project-config/services/request/request";

interface IFetchProps {
  extraData?: any;
  resolve?: (result: RequestResponse) => void;
  reject?: (e: any) => void;
}

export function useDynamicForm<
  IData extends object = any,
  T extends Record<string, any> = Record<string, any>
>({
  defaultPageSize = 15,
  defaultPageNum = 1,
  api,
  formatter,
  noPagination = false,
  isInvite = false,
}: {
  api: string;
  defaultPageSize?: number;
  defaultPageNum?: number;
  formatter?: Record<string, (v: any) => any>;
  noPagination?: boolean;
  isInvite?: boolean;
}) {
  const [form] = useForm<T>();
  const [tableData, setTableData] = useState<IData[]>([]);
  const [pageNum, setPageNum] = useState(defaultPageNum);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [total, setTotal] = useState(0);

  const [inviteNum, setInviteNum] = useState(0);
  const [couponNum, setCouponNum] = useState(0);
  const [earnVoucherNum, setEarnVoucherNum] = useState(0);

  const getSearchParams = useCallback(
    (extraData?: Record<string, any>) => {
      let params = form.getFieldsValue();

      if (formatter) {
        params = Object.entries(formatter).reduce((result, [key, fn]) => {
          if (result[key] !== undefined) {
            const formattedValue = fn(result[key]);

            const isArray = Array.isArray(formattedValue);
            // 返回对象则用返回对象替换原来字段
            if (typeof formattedValue === "object" && !isArray) {
              return { ...result, ...formattedValue, [key]: undefined };
            }

            // 否则替换原来的值
            return { ...result, [key]: formattedValue };
          }
          return result;
        }, params);
      }
      if (extraData?.hiddenPageSize) {
        const { hiddenPageSize, ...data } = extraData;
        return { ...params, ...data };
      }
      return { ...params, pageSize, pageNum, ...extraData };
    },
    [form, formatter, pageNum, pageSize]
  );

  const fetchData = useCallback(
    async (props?: IFetchProps) => {
      const { extraData, resolve, reject } = props || {};

      const allParams = getSearchParams(extraData);

      try {
        if (noPagination) {
          const result = await request.post<IData[]>(api, allParams);

          setTableData(result.data ?? []);
          setTotal(result.data?.length ?? 0);

          resolve?.(result);
          return;
        }
        if (isInvite) {
          let result = await request.post<{
            result: IData[];
            inviteNum: number;
            earnRewardsAmount: number;
            earnVoucherNum: number;
            total: number;
          }>(api, allParams);
          setTableData(result.data?.result ?? []);
          setTotal(result.data?.total ?? 0);
          setInviteNum(result.data?.inviteNum ?? 0);
          setCouponNum(result.data?.earnRewardsAmount ?? 0);
          setEarnVoucherNum(result.data?.earnVoucherNum ?? 0);
          resolve?.(result);
          return;
        }
        const result = await request.post<{ total: number; items: IData[] }>(
          api,
          allParams
        );

        setTableData(result.data?.items ?? []);
        setTotal(result.data?.total ?? 0);

        resolve?.(result);
      } catch (e) {
        reject?.(e);
      }
    },
    [api, getSearchParams, noPagination, isInvite]
  );

  return {
    form,
    getSearchParams,
    fetchData,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    tableData,
    total,
    inviteNum,
    couponNum,
    earnVoucherNum,
  };
}
